/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useRef } from "react";

import { Typography, useTheme } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import { JobStatusesService } from "services/jobStatuses/job-statuses-service";
import { useNotification } from "@hooks/useNotifications";
import { JobStatus } from "services/jobStatuses/types/jobStatus";
import { tokenManager } from "@libs/token-manager";
import { isUnauthorizedError } from "@libs/utils";

import { waitingOverlayText } from "./styles";

import { useWaitingOverlay } from ".";

const WaitingOverlay: React.FC = () => {
  const theme = useTheme();
  const { getJobStatuses } = new JobStatusesService();
  const updateControllerRef = useRef<AbortController | null>();
  const { showError } = useNotification();

  const { waitingOverlay, showJobProgress, hideJobProgress } = useWaitingOverlay();

  useEffect(() => {
    checkUpdates();
    return () => {
      if (updateControllerRef.current) {
        updateControllerRef.current.abort();
        updateControllerRef.current = null;
      }
      if (checkUpdatesTimeout.current) {
        clearTimeout(checkUpdatesTimeout.current);
      }
    };
  }, []);

  const checkUpdatesTimeout = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const startCheckUpdatesTimeout = () => {
    if (checkUpdatesTimeout.current) {
      clearTimeout(checkUpdatesTimeout.current);
    }
    checkUpdatesTimeout.current = setTimeout(() => checkUpdates(), window.appConfig.jobs.syncTimeout);
  };

  const checkUpdates = async () => {
    // Prevent checking updates when the tab is inactive
    if (document.visibilityState !== "visible" || tokenManager.isSyncing) {
      startCheckUpdatesTimeout();
      return;
    }
    const controller = new AbortController();
    let isAuthorized = true;

    try {
      updateControllerRef.current = controller;

      const result = await getJobStatuses(controller);
      if (result.some(i => i.status === JobStatus.InProgress)) {
        showJobProgress(result.find(i => i.status === JobStatus.InProgress));

        // Dispatch event to notify other components that job is in progress
        document.dispatchEvent(new Event("job-status:in-progress"));
      } else {
        hideJobProgress();
      }
    } catch (error: any) {
      if (isUnauthorizedError(error?.response?.status)) {
        isAuthorized = false;

        return;
      }
      if (!controller?.signal.aborted && error.code !== "ECONNREFUSED") {
        showError("Error while getting site updates.");
      }
    } finally {
      if (isAuthorized) {
        startCheckUpdatesTimeout();
      }
    }
  };

  return waitingOverlay ? (
    <div>
      <Backdrop sx={{ color: theme.palette.common.white, zIndex: 9999 }} open>
        <Typography variant="h5" sx={waitingOverlayText}>
          {waitingOverlay.text}
        </Typography>
      </Backdrop>
    </div>
  ) : null;
};

export default WaitingOverlay;
